import React from "react";

import { ModalContext } from "../../../libs/ModalProvider";
import { Context, IClassParam } from "../../../managerStore";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";
import Confirm from "../../../molecules/Confirm";
import { IClass, ITeacher } from "../../../state";

import styles from "./ClassForm.module.scss";

export interface ClassFormProps {
  title: string;
  calling: boolean;
  error: string;
  cls?: IClass;
  teacher_ids: number[];
  teachers: ITeacher[];
  isNextYear: boolean;
  upsertClass: (cls: IClassParam) => void;
  deleteClass?: (id: number) => void;
}

const validates: { [key: string]: (value: any) => string } = {};

validates["name"] = val => {
  return val.name ? "" : "クラス名を入力してください。";
};

validates["grade"] = val => {
  return val.grade > 0 ? "" : "学年を入力してください。";
};

export const ClassForm = (props: ClassFormProps) => {
  const ctx = React.useContext(ModalContext);
  const {
    state: { manager: managerState },
  } = React.useContext(Context);
  const { currentYear, nextYear } = useCurrentYear();
  const maxGrade = React.useMemo(
    () => managerState.school?.max_grade || 3,
    [managerState.school],
  );
  const [state, setState] = React.useState({
    id: props.cls ? props.cls.id : 0,
    year: props.isNextYear ? nextYear : currentYear,
    name: props.cls ? props.cls.name : "",
    grade: props.cls ? props.cls.grade : 0,
    is_display_full_name: props.cls ? props.cls.is_display_full_name : true,
    teacher_ids: props.teacher_ids,
    isSubmitted: false,
    errorMessages: {} as { [key: string]: string },
  });

  React.useEffect(() => {
    setState(s => {
      if (props.cls) {
        return {
          ...s,
          id: props.cls.id,
          name: props.cls.name,
          grade: props.cls.grade,
          is_display_full_name: props.cls.is_display_full_name,
        };
      } else {
        return {
          ...s,
        };
      }
    });
  }, [props.cls]);

  React.useEffect(() => {
    setState(s => ({ ...s, teacher_ids: props.teacher_ids }));
  }, [props.teacher_ids]);

  const onSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const errorMessages = {} as { [key: string]: string };
      Object.keys(validates).forEach(key => {
        const ret = validates[key](state);
        if (ret) {
          errorMessages[key] = ret;
        }
      });
      if (Object.keys(errorMessages).length > 0) {
        setState(s => ({ ...s, errorMessages }));
        return;
      }
      props.upsertClass({
        id: state.id,
        year: state.year,
        name: state.name,
        grade: state.grade,
        is_display_full_name: state.is_display_full_name,
        teacher_ids: state.teacher_ids,
        is_sub: false,
      });
    },
    [state, props],
  );

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const key = e.currentTarget.name;
      const val =
        e.currentTarget.type === "select"
          ? parseInt(e.currentTarget.value, 10)
          : e.currentTarget.type === "radio"
          ? e.currentTarget.value === "true"
          : e.currentTarget.value;
      const errorMessages = {} as { [key: string]: string };
      setState(prevState => {
        const args = { ...prevState, [key]: val };
        Object.keys(validates).forEach(key => {
          const ret = validates[key](args);
          errorMessages[key] = ret;
        });
        return {
          ...args,
          errorMessages,
        };
      });
    },
    [],
  );

  return (
    <div className="container">
      <div className={styles.pageTitle}>{props.title}</div>
      <div className={styles.inputAreaWrapper}>
        <form
          onSubmit={onSubmit}
          className={state.isSubmitted ? "was-validated" : ""}
        >
          {props.error && (
            <div className={styles.errorBillboard}>{props.error}</div>
          )}
          <div className="mb-3">
            <label className="form-label" htmlFor="grade">
              学年
            </label>
            <select
              name="grade"
              className={
                "form-select " +
                (state.errorMessages["grade"]
                  ? "is-invalid"
                  : state.grade
                  ? "is-valid"
                  : "")
              }
              aria-label="select"
              value={state.grade}
              onChange={onChange}
            >
              <option value={0}>選択してください</option>
              {[...Array(maxGrade)].map((v, i) => {
                const m = i + 1;
                return (
                  <option key={m} value={m}>
                    {m}年
                  </option>
                );
              })}
            </select>
            <div className="invalid-feedback">
              {state.errorMessages["grade"]}
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="name">
              クラス名
            </label>
            <div className="input-group">
              <input
                type="text"
                id="name"
                name="name"
                className={
                  "form-control " +
                  (state.errorMessages["name"]
                    ? "is-invalid"
                    : state.name
                    ? "is-valid"
                    : "")
                }
                value={state.name}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["name"]}
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="is_display_full_name">
              クラス名に学年をつける
            </label>
            <div className={`input-group ${styles.fullName}`}>
              <input
                id="is_display_full_name"
                name="is_display_full_name"
                type="checkbox"
                checked={state.is_display_full_name}
                onChange={e =>
                  setState({
                    ...state,
                    is_display_full_name: e.currentTarget.checked,
                  })
                }
              />
              {state.is_display_full_name && state.grade > 0
                ? `${state.grade}-${state.name}`
                : state.name}
            </div>
          </div>
          <p>グループに所属する先生を選択/解除してください</p>
          <div className={styles.table}>
            <table className="table table-striped table-hover mb-0">
              <thead>
                <tr>
                  <th>名前</th>
                  <th>Email</th>
                  <th>このグループの先生にする</th>
                </tr>
              </thead>
              <tbody className={styles.tbody}>
                {props.teachers.map(t => {
                  return (
                    <tr key={t.id}>
                      <td>{`${t.last_name} ${t.first_name}`}</td>
                      <td>{t.email}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={state.teacher_ids.indexOf(t.id) !== -1}
                          value={t.id}
                          onChange={() => {
                            if (state.teacher_ids.indexOf(t.id) === -1) {
                              setState({
                                ...state,
                                teacher_ids: [...state.teacher_ids, t.id].sort(
                                  (a, b) => a - b,
                                ),
                              });
                            } else {
                              setState({
                                ...state,
                                teacher_ids: state.teacher_ids.filter(
                                  id => id !== t.id,
                                ),
                              });
                            }
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={
              Object.keys(state.errorMessages).filter(
                k => state.errorMessages[k],
              ).length > 0 || props.calling
            }
          >
            {state.id === 0 ? "登録" : "更新"}
          </button>
        </form>
        {props.cls && props.deleteClass && (
          <div className={styles.deleteArea}>
            {props.cls.can_destroy ? (
              <p>まだ利用データがない場合、削除することができます</p>
            ) : (
              <p>利用データがあるため削除できません</p>
            )}
            <button
              className="btn btn-danger"
              onClick={() => {
                const { id, full_name } = props.cls!;
                ctx.actions.showModal(
                  <Confirm
                    calling={props.calling}
                    closeModal={() => {
                      ctx.actions.closeModal();
                    }}
                    content={
                      <>
                        <p className="mb-0">{full_name}を削除します。</p>
                        <p>本当に削除してもよろしいですか？</p>
                      </>
                    }
                    variant="danger"
                    callback={() => {
                      props.deleteClass!(id);
                      ctx.actions.closeModal();
                    }}
                  />,
                );
              }}
              disabled={!props.cls.can_destroy || props.calling}
            >
              削除
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassForm;
